import React, { useEffect, useState } from 'react';
import { Icon, Button, Skeleton } from '@gerdau/hefesto';
import { TableMenu, Tag } from '@gerdau/hefesto-mais-web';
import { Variants } from '@gerdau/hefesto/_shared/types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  openNewConstructionModal,
  setHighlightNewContracts
} from '../../../../store/ducks/contracts-and-constructions/actions';
import { IContractsManagerState } from '../../../../store/ducks/contracts-and-constructions/types';
import {
  EFabricationContractsStatus,
  IFabricationContract
} from '../../../../store/ducks/fabrication-contracts/types';
import ExpandedContractRowContent from './ExpandedContractRowContent';
import {
  ConstructionCustomTableCell,
  ContractTableContainer,
  TableContainer,
  TableMenuContainer
} from './styles';
import ViewOptionsButton from './ViewOptionsButton';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ApplicationState } from '../../../../store';
import Table from '../../../../components/Table';

interface ContractItemTableDataSource {
  key: number;
  contractNumber: string;
  constructionName: string;
  creationDate: moment.Moment;
  status: EFabricationContractsStatus;
  ordersDeployed: number;
  responsibleOfConstruction: string;
  item: IFabricationContract;
}

interface IContractsAndConstructionsTableProps {
  filteredContracts: IFabricationContract[];
  contractsManager: IContractsManagerState;
  isAllDisabled: boolean;
}

const ContractsAndConstructionsTable: React.FC<
  IContractsAndConstructionsTableProps
> = ({ filteredContracts, contractsManager, isAllDisabled }) => {
  const dispatch = useDispatch();

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );

  const [contractItemData, setContractItemData] = useState<
    ContractItemTableDataSource[]
  >([]);

  useEffect(() => {
    setContractItemData(
      convertIFabricationContractToContractsItensData(filteredContracts)
    );
  }, [filteredContracts]);

  const convertIFabricationContractToContractsItensData = (
    dataContracts: IFabricationContract[]
  ) => {
    return dataContracts.map((contract, idx) => ({
      key: idx,
      contractNumber: contract.contractNumber,
      constructionName: contract.customerOrder,
      creationDate: moment(contract.creationDate, 'YYYY-MM-DD'),
      status: contract.status,
      ordersDeployed: contract.entryOrders || 0,
      responsibleOfConstruction:
        contract.logistic &&
        contract.logistic.responsiblesOfConstruction.length > 0
          ? contract.logistic.responsiblesOfConstruction[0].name
          : ' - ',
      options: contract.contractNumber,
      item: contract
    }));
  };

  const contractsItensData: ContractItemTableDataSource[] =
    convertIFabricationContractToContractsItensData(filteredContracts);

  const [alreadyHighlighted, setAlreadyHighlighted] = useState(false);

  const setHighlightInRows = () => {
    setAlreadyHighlighted(true);

    const toggleHighlight = (item: ContractItemTableDataSource) => {
      const highlightTds = document.querySelectorAll(
        `#customer-contracts-table table > tbody > tr[data-row-key="${item.key}"] > td`
      );
      highlightTds.forEach((td) => {
        setTimeout(() => {
          td.classList.toggle('highlight-row');
        }, 100);
      });
    };

    const scrollToFirstNewContract = (
      firstNewContractItem: ContractItemTableDataSource
    ) => {
      const tr = document.querySelector(
        `#customer-contracts-table table > tbody > tr[data-row-key="${firstNewContractItem.key}"]`
      );
      setTimeout(() => {
        tr?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 100);
    };

    const newContractItems = contractsItensData.filter(
      (item) => item.status === EFabricationContractsStatus.NEW_CONTRACT
    );

    newContractItems.forEach((item) => toggleHighlight(item));

    if (newContractItems.length > 0) {
      scrollToFirstNewContract(newContractItems[0]);
    }

    setTimeout(() => {
      newContractItems.forEach((item) => toggleHighlight(item));
      dispatch(setHighlightNewContracts(false));
      setAlreadyHighlighted(false);
    }, 2500);
  };

  useEffect(() => {
    if (
      contractsManager.highlightNewContracts &&
      contractsItensData.length > 0 &&
      !alreadyHighlighted
    ) {
      setHighlightInRows();
    }
  });

  const variantMap = new Map<string, string>();
  variantMap.set(EFabricationContractsStatus.ALL, 'primary');
  variantMap.set(EFabricationContractsStatus.CLOSED, 'neutral');
  variantMap.set(EFabricationContractsStatus.NEW_CONTRACT, 'primary');
  variantMap.set(EFabricationContractsStatus.OPEN, 'info');

  const getColumns = () => {
    return [
      {
        title: 'Número do contrato',
        dataIndex: 'contractNumber',
        width: '10rem',
        sorter: (
          a: ContractItemTableDataSource,
          b: ContractItemTableDataSource
        ) => a.contractNumber.localeCompare(b.contractNumber),
        render: (text: string) => (
          <ConstructionCustomTableCell>{text}</ConstructionCustomTableCell>
        )
      },
      {
        title: 'Nome da obra',
        dataIndex: 'constructionName',
        width: '30rem',
        sorter: (
          a: ContractItemTableDataSource,
          b: ContractItemTableDataSource
        ) => a.constructionName.localeCompare(b.constructionName),
        render: (text: string) => (
          <ConstructionCustomTableCell>{text}</ConstructionCustomTableCell>
        )
      },
      {
        title: 'Data de criação',
        dataIndex: 'creationDate',
        width: '10rem',
        sorter: (
          a: ContractItemTableDataSource,
          b: ContractItemTableDataSource
        ) =>
          a.creationDate.toDate().getTime() - b.creationDate.toDate().getTime(),
        render: (creationDate: moment.Moment) => (
          <ConstructionCustomTableCell>
            {creationDate.format('DD/MM/YYYY')}
          </ConstructionCustomTableCell>
        )
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: '10rem',
        sorter: (
          a: ContractItemTableDataSource,
          b: ContractItemTableDataSource
        ) => a.status.localeCompare(b.status),
        render: (text: EFabricationContractsStatus) => (
          <Tag
            variant={
              variantMap.get(text) as Exclude<
                Variants,
                'secondary' | 'tertiary'
              >
            }
          >
            {text}
          </Tag>
        )
      },
      {
        title: 'Pedidos implantados',
        dataIndex: 'ordersDeployed',
        width: '10rem',
        sorter: (
          a: ContractItemTableDataSource,
          b: ContractItemTableDataSource
        ) => a.ordersDeployed - b.ordersDeployed,
        render: (ordersDeployed: number) => (
          <ConstructionCustomTableCell>
            {ordersDeployed || ' - '}
          </ConstructionCustomTableCell>
        )
      },
      {
        title: 'Responsável da obra',
        dataIndex: 'responsibleOfConstruction',
        width: '30rem',
        sorter: (
          a: ContractItemTableDataSource,
          b: ContractItemTableDataSource
        ) =>
          a.responsibleOfConstruction.localeCompare(
            b.responsibleOfConstruction
          ),
        render: (text: string) => (
          <ConstructionCustomTableCell>{text}</ConstructionCustomTableCell>
        )
      },
      {
        title: '',
        dataIndex: 'options',
        width: '1%',
        render: (contractNumber: string, data: ContractItemTableDataSource) => (
          <ConstructionCustomTableCell>
            <ViewOptionsButton
              contractsManager={contractsManager}
              contractNumber={contractNumber}
              contractStatus={data.status}
            />
          </ConstructionCustomTableCell>
        )
      }
    ];
  };

  const loading = contractsManager.loading || customersState.loading;

  return (
    <TableContainer>
      <TableMenuContainer>
        <TableMenu
          rightComponents={
            loading ? (
              <Skeleton type="input"></Skeleton>
            ) : (
              <Button
                name="Edit"
                type="button"
                variant="secondary"
                onClick={() => {
                  dispatch(
                    sendTagging({
                      screenName: 'LG:ContratosEObras:SelecaoDoEmissor',
                      itemName: 'button: cadastrar_nova_obra',
                      itemDescription:
                        'contratoEObras_selecaoDoEmissor_cadastrarNovaObra',
                      event: 'click'
                    })
                  );

                  dispatch(openNewConstructionModal());
                }}
                icon={<Icon variant="primary" option="Plus" />}
                disabled={
                  isAllDisabled ||
                  contractsManager.customerContracts.filter(
                    (contract) =>
                      contract.status ===
                      EFabricationContractsStatus.NEW_CONTRACT
                  ).length === 0
                }
              >
                Cadastrar nova obra
              </Button>
            )
          }
        ></TableMenu>
      </TableMenuContainer>
      <ContractTableContainer>
        <Table
          columns={getColumns()}
          data={contractItemData}
          expandedRowRender={(record) => (
            <ExpandedContractRowContent customerContract={record.item} />
          )}
          hasPagination
        />
      </ContractTableContainer>
    </TableContainer>
  );
};

export default ContractsAndConstructionsTable;
